<template>
  <div class="page-wrap">

    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header-left">
          <div class="panel-icon"><i class="fa fa-desktop"></i></div>
          <div>
            <div class="panel-title">
              Workstations
              <span class="count-badge" v-if="pagination.total_records > 0">
                {{ pagination.total_records }}
              </span>
            </div>
            <div class="panel-sub">Manage API keys and device workstations</div>
          </div>
        </div>
        <div class="panel-header-right" v-if="currentUser.u.roles == 'admin'">
          <button class="btn-create" @click="showModal">
            <i class="fa fa-plus"></i> Create Workstation
          </button>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="toolbar">
        <!-- Service Provider (admin) -->
        <div class="ts-wrap" v-if="currentUser.u.is_admin" ref="spWrap" v-click-outside="closeSpDropdown">
          <div class="ts-input" @click="toggleSp">
            <span class="ts-val" v-if="!spOpen">{{ spLabel }}</span>
            <input v-if="spOpen" type="text" class="ts-search" v-model="spSearch" placeholder="Search provider…"
              ref="spInput" @keydown.esc="closeSpDropdown" />
            <i class="fa fa-chevron-down ts-arrow" :class="{ open: spOpen }"></i>
          </div>
          <div class="ts-dropdown" v-if="spOpen" :style="spDropStyle">
            <div class="ts-item" @click="selectSp('all')">All Service Providers</div>
            <div class="ts-item" v-for="(c, i) in filteredSp" :key="i" @click="selectSp(c.id)">{{ c.name }}</div>
            <div class="ts-empty" v-if="filteredSp.length === 0 && spSearch">No match</div>
          </div>
        </div>

        <!-- Location -->
        <div class="ts-wrap" ref="locWrap" v-click-outside="closeLocDropdown">
          <div class="ts-input" @click="toggleLoc">
            <span class="ts-val" v-if="!locOpen">{{ locLabel }}</span>
            <input v-if="locOpen" type="text" class="ts-search" v-model="locSearch" placeholder="Search location…"
              ref="locInput" @keydown.esc="closeLocDropdown" />
            <i class="fa fa-chevron-down ts-arrow" :class="{ open: locOpen }"></i>
          </div>
          <div class="ts-dropdown" v-if="locOpen" :style="locDropStyle">
            <div class="ts-item" @click="selectLoc('all')">All Locations</div>
            <div class="ts-item" v-for="(c, i) in filteredLoc" :key="i" @click="selectLoc(c.id)">{{ c.name }}</div>
            <div class="ts-empty" v-if="filteredLoc.length === 0 && locSearch">No match</div>
          </div>
        </div>

        <div class="search-box">
          <i class="fa fa-search"></i>
          <input type="text" placeholder="Filter by label…" v-model="labelSearch" />
        </div>

        <button class="btn-search" @click="loadApiData()">
          <i class="fa fa-search"></i> Search
        </button>
      </div>

      <!-- Loading -->
      <div class="state-box" v-if="loadingData">
        <i class="fa fa-spinner fa-spin"></i>
        Loading workstations…
      </div>

      <!-- Empty -->
      <div class="state-box" v-else-if="filteredData.length === 0">
        <i class="fa fa-desktop"></i>
        No workstations found
      </div>

      <!-- Table -->
      <div class="table-wrap" v-else>
        <table class="ws-table">
          <thead>
            <tr>
              <th style="width:40px;">#</th>
              <th style="min-width:140px;">Label</th>
              <th style="min-width:200px;">Token</th>
              <th style="min-width:120px;">Device ID</th>
              <th style="min-width:120px;">Serial No.</th>
              <th style="min-width:130px;">Allowed IPs</th>
              <th style="width:80px; text-align:center;">Status</th>
              <th style="min-width:140px;">Location</th>
              <th v-if="currentUser.u.roles == 'admin'" style="width:54px; text-align:right;">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in filteredData" :key="index">
              <td class="col-num">{{ (pagination.current_page - 1) * 15 + index + 1 }}</td>
              <td class="col-label">{{ item.label }}</td>
              <td class="col-token">
                <span class="token-text">{{ item.api_key }}</span>
                <button class="btn-copy" :title="'Copy token'" @click="copyToken(item.api_key, index)">
                  <i :class="copiedIndex === index ? 'fa fa-check' : 'fa fa-copy'"></i>
                </button>
              </td>
              <td class="col-mono">{{ item.local_device_id || '—' }}</td>
              <td class="col-mono">{{ item.serial_number || '—' }}</td>
              <td>{{ item.ip_address || '—' }}</td>
              <td style="text-align:center;">
                <span :class="['badge-pill', item.is_active ? 'active' : 'inactive']">
                  <i :class="item.is_active ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
                  {{ item.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="col-location">{{ item.client_name || '—' }}</td>
              <td v-if="currentUser.u.roles == 'admin'" style="text-align:right; overflow:visible;" @click.stop>
                <div class="action-wrap" :class="{ open: openIndex === index }"
                  @click.stop="toggleDropdown(index, $event)" :ref="`dropdown-${index}`">
                  <div class="action-btn">•••</div>
                  <div class="action-menu" :style="openIndex === index ? menuStyle : null">
                    <div class="menu-item blue" @click.stop="view(item)">
                      <i class="fa fa-eye"></i> View
                    </div>
                    <div class="menu-item orange" @click.stop="edit(item)">
                      <i class="fa fa-pencil-alt"></i> Edit
                    </div>
                    <div class="menu-item divider"></div>
                    <div class="menu-item danger" @click.stop="confirmDeleteModal(item)">
                      <i class="fa fa-trash"></i> Delete
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-bar" v-if="pagination.total_page > 1">
        <span class="pg-info">
          Page {{ pagination.current_page }} of {{ pagination.total_page }}
          <span v-if="pagination.total_records"> · {{ pagination.total_records }} total</span>
        </span>
        <div class="pg-controls">
          <button class="pg-btn" :disabled="pagination.current_page === 1" @click="firstPage()">
            <i class="fa fa-angle-double-left"></i>
          </button>
          <button class="pg-btn" :disabled="pagination.current_page === 1" @click="previousPage()">
            <i class="fa fa-angle-left"></i>
          </button>
          <button v-for="p in visiblePages" :key="p" class="pg-btn" :class="{ active: p === pagination.current_page }"
            @click="selectedPage(p)">{{ p }}</button>
          <button class="pg-btn" :disabled="pagination.current_page === pagination.total_page" @click="nextPage()">
            <i class="fa fa-angle-right"></i>
          </button>
          <button class="pg-btn" :disabled="pagination.current_page === pagination.total_page" @click="lastPage()">
            <i class="fa fa-angle-double-right"></i>
          </button>
        </div>
      </div>

    </div>

    <!-- Workstation modal -->
    <apikey :key="key" v-if="ModalShow" v-on:closemodal="closeModal" :show-modal="ModalShow" :apikey="apikeyData"
      :is_edit="is_edit" :apikeyid="apikeyid" :is_view="is_view" :is_disabled="is_disabled"></apikey>

    <!-- Delete Confirmation Modal -->
    <div class="del-overlay" v-if="delModal.show" @click.self="delModal.show = false">
      <div class="del-card">
        <div class="del-head">
          <div class="del-icon"><i class="fa fa-trash-alt"></i></div>
          <div class="del-title">Delete Workstation</div>
        </div>
        <div class="del-body">
          <p class="del-msg">
            You are about to permanently delete<br />
            <strong>{{ delModal.label }}</strong>
          </p>
          <div class="del-warn">
            <i class="fa fa-exclamation-circle"></i>
            This action cannot be undone
          </div>
        </div>
        <div class="del-foot">
          <button class="btn-del-cancel" @click="delModal.show = false">
            <i class="fa fa-times"></i> Cancel
          </button>
          <button class="btn-del-confirm" @click="doDelete()">
            <i class="fa fa-trash"></i> Delete
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import {
  FETCH_APIKEY,
  CREATE_APIKEY,
  DELETE_APIKEY,
  FETCH_CLIENTS,
  FETCH_SERVICES_CLIENTS
} from "@/core/services/store/actions.type";
import apikey from "@/pages/backend/apikey/apikeymodal";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";

export default {
  directives: {
    clickOutside: {
      bind(el, binding) {
        el._clickOutsideHandler = e => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener("mousedown", el._clickOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener("mousedown", el._clickOutsideHandler);
      }
    }
  },
  computed: {
    ...mapGetters(["currentUser", "getApikeyData", "getClients", "getSpClients"]),
    filteredSp() {
      const q = (this.spSearch || "").toLowerCase();
      const list = this.getClients || [];
      return q ? list.filter(c => c.name && c.name.toLowerCase().includes(q)) : list;
    },
    spLabel() {
      if (this.serviceProvider === "all") return "All Service Providers";
      const c = (this.getClients || []).find(c => c.id === this.serviceProvider);
      return c ? c.name : "All Service Providers";
    },
    filteredLoc() {
      const q = (this.locSearch || "").toLowerCase();
      const list = this.currentUser.u.is_admin ? (this.getSpClients || []) : (this.getClients || []);
      return q ? list.filter(c => c.name && c.name.toLowerCase().includes(q)) : list;
    },
    locLabel() {
      if (this.serviceOutlet === "all") return "All Locations";
      const list = this.currentUser.u.is_admin ? (this.getSpClients || []) : (this.getClients || []);
      const c = list.find(c => c.id === this.serviceOutlet);
      return c ? c.name : "All Locations";
    },
    filteredData() {
      const list = this.getApikeyData.data || [];
      if (!this.labelSearch.trim()) return list;
      const q = this.labelSearch.trim().toLowerCase();
      return list.filter(item => item.label && item.label.toLowerCase().includes(q));
    },
    visiblePages() {
      const pages = [];
      const cur = this.pagination.current_page;
      const total = this.pagination.total_page;
      for (let i = Math.max(1, cur - 2); i <= Math.min(total, cur + 2); i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  mounted() {
    this.pagination = {
      total_records: 0,
      total_page: 0,
      current_page: 1,
      per_page_data: 15,
      received_per_page_data: 15
    };
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.loadClients();
    this.loadApiData();
    document.addEventListener("click", this.closeDropdown);
    window.addEventListener("scroll", this.closeDropdown, true);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeDropdown);
    window.removeEventListener("scroll", this.closeDropdown, true);
  },
  components: { apikey },
  methods: {
    toggleSp() {
      this.spOpen = !this.spOpen;
      if (this.spOpen) {
        this.spSearch = "";
        this.$nextTick(() => {
          if (this.$refs.spInput) this.$refs.spInput.focus();
          const wrap = this.$refs.spWrap;
          if (wrap) {
            const r = wrap.getBoundingClientRect();
            this.spDropStyle = { position: "fixed", top: (r.bottom + 4) + "px", left: r.left + "px", width: r.width + "px", zIndex: 9999 };
          }
        });
      }
    },
    closeSpDropdown() { this.spOpen = false; this.spSearch = ""; this.spDropStyle = {}; },
    selectSp(id) {
      this.serviceProvider = id;
      this.closeSpDropdown();
      this.loadServicesProvider();
    },
    toggleLoc() {
      this.locOpen = !this.locOpen;
      if (this.locOpen) {
        this.locSearch = "";
        this.$nextTick(() => {
          if (this.$refs.locInput) this.$refs.locInput.focus();
          const wrap = this.$refs.locWrap;
          if (wrap) {
            const r = wrap.getBoundingClientRect();
            this.locDropStyle = { position: "fixed", top: (r.bottom + 4) + "px", left: r.left + "px", width: r.width + "px", zIndex: 9999 };
          }
        });
      }
    },
    closeLocDropdown() { this.locOpen = false; this.locSearch = ""; this.locDropStyle = {}; },
    selectLoc(id) { this.serviceOutlet = id; this.closeLocDropdown(); },
    loadServicesProvider() {
      this.serviceOutlet = "all";
      this.$store.dispatch(FETCH_SERVICES_CLIENTS, this.serviceProvider);
    },
    async loadApiData() {
      this.loadingData = true;
      let page = this.pagination.current_page;
      var data = {
        service_outlet: this.serviceOutlet,
        service_provider: this.serviceProvider,
        page: page
      };
      await this.$store.dispatch(FETCH_APIKEY, data).then(res => {
        this.pagination.total_records = res.total_record;
        this.pagination.total_page = res.page_range;
        this.pagination.received_per_page_data = res.data.length;
        this.loadingData = false;
      }).catch(() => { this.loadingData = false; });
    },
    showModal() {
      this.key += 1;
      this.apikeyData = {
        id: 0,
        label: "",
        api_key: "",
        client_id: "",
        ip_address: "",
        is_active: 0,
        do_username: "",
        do_lsid: "",
        do_host: "",
        do_port: "",
        do_nist_filepath: "",
        do_sig_filepath: "",
        do_channeler: "1",
      };
      this.ModalShow = true;
      this.is_edit = false;
      this.is_view = false;
      this.is_disabled = false;
    },
    closeModal() {
      this.key += 1;
      this.ModalShow = false;
      this.is_view = false;
      this.is_edit = false;
      this.is_disabled = false;
      this.loadApiData();
    },
    async loadClients() {
      await this.$store.dispatch(FETCH_CLIENTS);
    },
    edit(item) {
      this.key += 1;
      this.ModalShow = true;
      this.is_edit = true;
      this.is_view = false;
      this.is_disabled = false;
      this.apikeyid = item.id;
    },
    view(item) {
      this.key += 1;
      this.ModalShow = true;
      this.is_edit = true;
      this.is_view = true;
      this.is_disabled = true;
      this.apikeyid = item.id;
    },
    confirmDeleteModal(item) {
      this.delModal = { show: true, id: item.id, label: item.label };
    },
    doDelete() {
      this.$store.dispatch(DELETE_APIKEY, this.delModal.id).then(() => {
        this.$toastr.s("Workstation successfully deleted");
        this.delModal.show = false;
        this.pagination.current_page = 1;
        this.loadApiData();
      });
    },
    apidelete(item) {
      this.confirmDeleteModal(item);
    },
    copyToken(token, index) {
      navigator.clipboard.writeText(token).then(() => {
        this.copiedIndex = index;
        setTimeout(() => { this.copiedIndex = null; }, 1800);
        this.$toastr.s("Token copied to clipboard");
      });
    },
    gotoApiKeyDevices(id) {
      this.$router.push({
        name: "client.apikey.devices",
        params: { id, client_slug: this.currentUser.u.is_admin ? 'admin' : this.$route.params.client_slug }
      });
    },
    toggleDropdown(index, event) {
      if (this.openIndex === index) {
        this.openIndex = null;
        this.menuStyle = null;
        return;
      }
      this.openIndex = index;
      this.$nextTick(() => {
        const rect = event.currentTarget.getBoundingClientRect();
        const menuH = 130;
        const rightOffset = window.innerWidth - rect.right;
        if (window.innerHeight - rect.bottom < menuH + 8) {
          this.menuStyle = { position: 'fixed', bottom: (window.innerHeight - rect.top + 4) + 'px', top: 'auto', right: rightOffset + 'px' };
        } else {
          this.menuStyle = { position: 'fixed', top: (rect.bottom + 4) + 'px', bottom: 'auto', right: rightOffset + 'px' };
        }
      });
    },
    closeDropdown() {
      this.openIndex = null;
      this.menuStyle = null;
    },
    selectedPage(n) { this.pagination.current_page = n; this.loadApiData(); },
    nextPage() { this.pagination.current_page++; this.loadApiData(); },
    lastPage() { this.pagination.current_page = this.pagination.total_page; this.loadApiData(); },
    firstPage() { this.pagination.current_page = 1; this.loadApiData(); },
    previousPage() { this.pagination.current_page--; this.loadApiData(); },
    update(item) {
      let data = { ...item };
      this.$store.dispatch(CREATE_APIKEY, data)
        .then(() => { this.$toastr.s("Workstation updated"); this.loadApiData(); })
        .catch(err => { this.errors = err; });
    }
  },
  data() {
    return {
      loadingData: false,
      ModalShow: false,
      copiedIndex: null,
      openIndex: null,
      menuStyle: null,
      pagination: {
        total_records: 0,
        total_page: 0,
        current_page: 1,
        per_page_data: 15,
        received_per_page_data: 15
      },
      spOpen: false, spSearch: "", spDropStyle: {},
      locOpen: false, locSearch: "", locDropStyle: {},
      is_view: false,
      is_disabled: false,
      key: 0,
      serviceOutlet: "all",
      serviceProvider: "all",
      labelSearch: "",
      is_edit: false,
      apikeyData: {
        id: 0,
        label: "",
        api_key: "",
        client: "",
        ip_address: false,
        is_active: 0,
        do_username: "",
        do_host: "",
        do_port: "",
        do_nist_filepath: "",
        do_sig_filepath: "",
        do_key_filepath: "",
        do_channeler: "1",
      },
      apikeyid: 0,
      delModal: { show: false, id: null, label: "" },
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Workstation List", route: "" }
      ]
    };
  }
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;

.page-wrap {
  padding: 8px 0 80px;
  font-family: $font;
}

.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: visible;
}

/* ── Header ── */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #fafbfd;
  border-bottom: 1px solid #eef0f6;
  gap: 10px;
  flex-wrap: wrap;
}

.panel-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.panel-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: $blue;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.panel-title {
  font-size: 14px;
  font-weight: 700;
  color: $dark;
  display: flex;
  align-items: center;
  gap: 8px;
}

.count-badge {
  font-size: 11px;
  font-weight: 600;
  color: #7a8499;
  background: #eef0f6;
  border-radius: 10px;
  padding: 1px 7px;
}

.panel-sub {
  font-size: 11px;
  color: #8a94a6;
}

.btn-create {
  height: 34px;
  padding: 0 16px;
  background: #F58020;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
}

/* ── Toolbar ── */
.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-bottom: 1px solid #f0f3fa;
  flex-wrap: wrap;
}

.filter-select {
  height: 36px;
  border: 1.5px solid #dde2ec;
  border-radius: 8px;
  padding: 0 28px 0 10px;
  font-size: 13px;
  color: $dark;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23a0aab8'/%3E%3C/svg%3E") no-repeat right 10px center;
  appearance: none;
  outline: none;
  cursor: pointer;
  font-family: $font;
  transition: border-color 0.2s;

  &:focus {
    border-color: $blue;
  }
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 160px;
  max-width: 240px;

  i {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    color: #a0aab8;
    font-size: 12px;
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 36px;
    border: 1.5px solid #dde2ec;
    border-radius: 8px;
    padding: 0 12px 0 30px;
    font-size: 13px;
    font-family: $font;
    color: $dark;
    outline: none;
    transition: border-color 0.2s;

    &::placeholder {
      color: #b0bac9;
    }

    &:focus {
      border-color: $blue;
      box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
    }
  }
}

.btn-search {
  height: 36px;
  padding: 0 16px;
  background: $blue;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
}

/* ── Type-and-select ── */
.ts-wrap {
  position: relative;
  min-width: 180px;
  max-width: 240px;
}

.ts-input {
  display: flex;
  align-items: center;
  height: 36px;
  border: 1.5px solid #dde2ec;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
  cursor: pointer;
  gap: 6px;
  transition: border-color 0.2s;

  &:hover {
    border-color: $blue;
  }

  &:focus-within {
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
  }
}

.ts-val {
  flex: 1;
  font-size: 13px;
  color: $dark;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
}

.ts-search {
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  color: $dark;
  font-family: $font;
  background: transparent;
  min-width: 0;

  &::placeholder {
    color: #b0bac9;
  }
}

.ts-arrow {
  font-size: 10px;
  color: #a0aab8;
  flex-shrink: 0;
  transition: transform 0.2s;
  pointer-events: none;

  &.open {
    transform: rotate(180deg);
  }
}

.ts-dropdown {
  position: fixed;
  background: #fff;
  border: 1.5px solid #dde2ec;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(13, 27, 62, 0.12);
  z-index: 9999;
  max-height: 220px;
  overflow-y: auto;
}

.ts-item {
  padding: 8px 12px;
  font-size: 13px;
  color: $dark;
  font-family: $font;
  cursor: pointer;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
  transition: background 0.12s;

  &:hover {
    background: #f0f7fd;
    color: $blue;
  }
}

.ts-empty {
  padding: 10px 12px;
  font-size: 12px;
  color: #a0aab8;
  text-align: center;
}

/* ── States ── */
.state-box {
  text-align: center;
  padding: 48px 24px;
  color: #a0aab8;
  font-size: 13px;

  i {
    font-size: 28px;
    display: block;
    margin-bottom: 10px;
    opacity: 0.45;
  }
}

/* ── Table ── */
.table-wrap {
  overflow-x: auto;
}

.ws-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: $font;
  table-layout: auto;

  thead tr th {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #7a8499;
    padding: 9px 12px;
    border-bottom: 2px solid #eef0f8;
    background: #fff;
    white-space: nowrap;
  }

  tbody tr {
    border-bottom: 1px solid #f5f7fa;
    transition: background 0.15s;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #f8fbff;
    }

    td {
      padding: 9px 12px;
      color: #2d3748;
      vertical-align: middle;
    }
  }
}

.col-num {
  color: #a0aab8;
  font-size: 12px;
}

.col-label {
  font-weight: 500;
  color: $dark;
}

.col-token {
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 220px;
}

.token-text {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: #4a5568;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.btn-copy {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: 1.5px solid #dde2ec;
  border-radius: 5px;
  background: #f7f9fc;
  color: #7a8499;
  font-size: 11px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    border-color: $blue;
    color: $blue;
    background: #eef2fb;
  }

  .fa-check {
    color: #276749;
  }
}

.col-mono {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #4a5568;
}

.col-location {
  font-size: 12px;
  color: #4a5568;
}

/* ── Badges ── */
.badge-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  &.active {
    background: #c6f6d5;
    color: #276749;
  }

  &.inactive {
    background: #fed7d7;
    color: #c53030;
  }
}

/* ── Action dropdown ── */
.action-wrap {
  position: relative;
  display: inline-block;

  .action-btn {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background: #f5f7fa;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    font-size: 16px;
    font-weight: 700;
    color: #4a5568;
    letter-spacing: 1px;
    transition: background 0.15s;

    &:hover {
      background: #eef2fb;
      color: #185FA5;
      border-color: #c5d4f0;
    }
  }

  .action-menu {
    display: none;
    position: fixed;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    min-width: 140px;
    box-shadow: 0 8px 24px rgba(13, 27, 62, 0.12);
    z-index: 9999;
    overflow: hidden;

    .menu-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 9px 14px;
      font-size: 13px;
      color: #2d3748;
      cursor: pointer;
      transition: background 0.15s;

      i {
        width: 16px;
        font-size: 13px;
      }

      &:hover {
        background: #f8fbff;
      }

      &.blue {
        color: #185FA5;

        i {
          color: #185FA5;
        }

        &:hover {
          background: #eef2fb;
        }
      }

      &.teal {
        color: #276749;

        i {
          color: #276749;
        }

        &:hover {
          background: #f0fdf4;
        }
      }

      &.orange {
        color: #c05621;

        i {
          color: #c05621;
        }

        &:hover {
          background: #fff7ed;
        }
      }

      &.danger {
        color: #c62828;

        i {
          color: #c62828;
        }

        &:hover {
          background: #fff5f5;
        }
      }

      &.divider {
        border-top: 1px solid #f0f3fa;
        pointer-events: none;
        padding: 0;
        margin: 0;
      }
    }
  }

  &.open .action-menu {
    display: block;
  }
}

/* ── Pagination ── */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid #f0f3fa;
  flex-wrap: wrap;
  gap: 10px;
}

.pg-info {
  font-size: 12px;
  color: #7a8499;
}

.pg-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pg-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1.5px solid #dde2ec;
  background: #fff;
  color: #4a5568;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    border-color: $blue;
    color: $blue;
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &.active {
    background: $blue;
    border-color: $blue;
    color: #fff;
  }
}

/* ── Delete modal ── */
.del-overlay {
  position: fixed;
  inset: 0;
  background: rgba(13, 27, 62, 0.52);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9100;
  backdrop-filter: blur(3px);
  padding: 20px;
  font-family: $font;
}

.del-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 24px 64px rgba(13, 27, 62, 0.22);
  width: 100%;
  max-width: 380px;
  overflow: hidden;
}

.del-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 24px 16px;
  background: #fff5f5;
  border-bottom: 1px solid #fed7d7;
}

.del-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fed7d7;
  color: #c62828;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin-bottom: 12px;
}

.del-title {
  font-size: 15px;
  font-weight: 700;
  color: #c62828;
}

.del-body {
  padding: 20px 24px 8px;
  text-align: center;
}

.del-msg {
  font-size: 13px;
  color: #4a5568;
  line-height: 1.6;
  margin: 0 0 6px;

  strong {
    color: $dark;
  }
}

.del-warn {
  font-size: 11px;
  color: #e53e3e;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-top: 8px;
}

.del-foot {
  display: flex;
  gap: 10px;
  padding: 16px 24px 24px;

  button {
    flex: 1;
    height: 40px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: $font;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: all 0.15s;
  }

  .btn-del-cancel {
    background: #fff;
    color: #4a5568;
    border: 1.5px solid #dde2ec;

    &:hover {
      border-color: $blue;
      color: $blue;
    }
  }

  .btn-del-confirm {
    background: #c62828;
    color: #fff;
    border: none;

    &:hover {
      background: #9b1c1c;
    }
  }
}
</style>

<style>
.swal2-popup .swal2-icon {
  margin-left: 39% !important;
}
</style>
